import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Button,
  Checkbox,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AdminArticleDetail,
  apiAdminGetArticleById,
  apiAdminUpdateArticle,
  apiAdminUploadPdf,
  ApiErrorResponse,
} from '../api/client';
import PortalPageShell from './PortalPageShell';

export default function AdminEditArticlePage() {
  const { id } = useParams();
  const articleId = Number(id);
  const navigate = useNavigate();

  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [published, setPublished] = useState(true);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const normalizedSlug = useMemo(() => {
    return slug.trim() || title.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  }, [slug, title]);

  useEffect(() => {
    const loadArticle = async () => {
      setLoading(true);
      const articleResponse: AdminArticleDetail | ApiErrorResponse = await apiAdminGetArticleById(articleId);

      if ('id' in articleResponse) {
        setTitle(articleResponse.title || '');
        setSlug(articleResponse.slug || '');
        setExcerpt(articleResponse.excerpt || '');
        setPublished(Boolean(articleResponse.published));
        setMessage('');
      } else {
        setMessage(articleResponse.message || 'Unable to load article details.');
      }

      setLoading(false);
    };

    if (articleId) {
      loadArticle();
    }
  }, [articleId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage('');
    setIsSubmitting(true);

    try {
      let pdfFilename: string | undefined;

      if (selectedFile) {
        const uploadResponse = await apiAdminUploadPdf(selectedFile);
        if (!uploadResponse?.success || !uploadResponse?.pdf_filename) {
          setMessage(uploadResponse?.message || 'PDF upload failed.');
          return;
        }
        pdfFilename = uploadResponse.pdf_filename;
      }

      const updatePayload: Record<string, unknown> = {
        title,
        slug: normalizedSlug,
        excerpt,
        published,
      };

      if (pdfFilename) {
        updatePayload.content_type = 'pdf';
        updatePayload.pdf_filename = pdfFilename;
      }

      const updateResponse = await apiAdminUpdateArticle(articleId, updatePayload);

      if (updateResponse?.success) {
        navigate(`/admin/articles/${articleId}`, { replace: true });
      } else {
        setMessage(updateResponse?.message || 'Failed to update article.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <PortalPageShell title="Edit Article" subtitle="Loading article details.">
        <Stack align="center" py={12} spacing={4}>
          <Spinner size="xl" color="blue.500" />
          <Text color="gray.600">Loading article...</Text>
        </Stack>
      </PortalPageShell>
    );
  }

  return (
    <PortalPageShell title="Edit Article" subtitle="Update article details and replace the PDF if needed.">
      <Stack as="form" spacing={5} onSubmit={handleSubmit}>
        {message ? (
          <Alert status="error" borderRadius="lg">
            <AlertIcon />
            {message}
          </Alert>
        ) : null}

        <FormControl isRequired>
          <FormLabel>Title</FormLabel>
          <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Article title" />
        </FormControl>

        <FormControl>
          <FormLabel>Slug</FormLabel>
          <Input value={slug} onChange={e => setSlug(e.target.value)} placeholder="article-slug" />
          <FormHelperText>Leave blank to generate from the title.</FormHelperText>
        </FormControl>

        <FormControl isRequired>
          <FormLabel>Excerpt</FormLabel>
          <Input value={excerpt} onChange={e => setExcerpt(e.target.value)} placeholder="Short summary" />
        </FormControl>

        <FormControl>
          <FormLabel>Replace PDF</FormLabel>
          <Input
            type="file"
            accept="application/pdf,.pdf"
            padding={1}
            onChange={e => setSelectedFile(e.target.files?.[0] || null)}
          />
          <FormHelperText>Upload only if you want to replace the existing PDF.</FormHelperText>
        </FormControl>

        <Checkbox isChecked={published} onChange={e => setPublished(e.target.checked)}>
          Published
        </Checkbox>

        <Button type="submit" colorScheme="blue" width="fit-content" isLoading={isSubmitting} loadingText="Saving changes">
          Save Changes
        </Button>
      </Stack>
    </PortalPageShell>
  );
}
